<template>
    <div class="json-editor-full">
        <div class="json-header">
            <div>
                <p class="json-subtitle">
                    JSON para Edição
                    <br>
                    <small><strong>Shift + Alt + F</strong> para formatar ou clique </small>
                    <i @click="formatJson" class="fa-solid fa-wand-magic-sparkles btn-icon"></i>
                </p>
            </div>

            <div class="json-buttons">
                <button @click="downloadJson" type="button" class="btn-secondary" title="Baixar JSON">
                    <i class="fa-solid fa-download btn-icon"></i>
                </button>

                <Button _key="send" title="Reenviar" type="primary" size="medium" :clicked="generate" />
            </div>
        </div>

        <div class="json-status-row">
            <span class="status-badge" :class="{
                'status-ok': validationStatus.valid,
                'status-error': !validationStatus.valid
            }">
                {{ validationStatus.message }}
            </span>

            <span class="json-meta">
                {{ jsonText.length }} caracteres
            </span>
        </div>

        <div class="json-body">
            <div v-if="error" class="json-error">
                {{ error }}
            </div>
            <textarea v-model="jsonText" class="json-textarea" spellcheck="false" @keydown="handleKeydown"></textarea>
        </div>

        <div v-if="isConnecting" class="connection-modal-backdrop">
            <div class="connection-modal">
                <vue-loading type="bubbles" color="#fff" :size="{ width: '26px', height: '26px' }"></vue-loading>
                <span>Conectando com o servidor...</span>
            </div>
        </div>
    </div>
</template>

<script>
import Button from "@nixweb/nixloc-ui/src/component/forms/Button";

import { mapActions, mapMutations } from "vuex";

export default {
    name: "JsonEditorInterpreter",
    components: {
        Button,
    },
    props: {
        jsonData: {
            type: [Object, Array, String],
            required: false,
            default: null,
        },
        source: {
            type: String,
            default: "nfse",
        },
        nf: Object,
    },
    data() {
        return {
            error: null,
            jsonText: "",
            downloadFilename: `nf_.json`,
            sampleJson: {},
            isConnecting: false,
        };
    },
    created() {
        this.initJsonText();
    },
    watch: {
        jsonData: {
            handler() {
                this.initJsonText();
            },
            deep: true,
        },
    },
    computed: {
        urlGenerateApi() {
            return `/api/v1/tax-document/${this.source}/generate-json`;
        },
        validationStatus() {
            if (!this.jsonText || !this.jsonText.trim()) {
                return {
                    valid: false,
                    message: "JSON vazio",
                };
            }

            try {
                JSON.parse(this.jsonText);
                return {
                    valid: true,
                    message: "JSON válido",
                };
            } catch (e) {
                return {
                    valid: false,
                    message: "JSON inválido",
                };
            }
        },
    },
    methods: {
        ...mapMutations("generic", ["addEvent", "addLoading", "removeLoading"]),
        ...mapActions("generic", ["putApi"]),
        generate() {
            if (this.isConnecting) {
                return;
            }

            this.error = null;
            this.isConnecting = true;
            this.addLoading("send");

            const params = { url: this.urlGenerateApi, obj: { id: this.nf.id, json: this.jsonText } };
            this.putApi(params)
                .then(() => {
                    this.addEvent({ name: "updateNf" });
                })
                .catch(() => {
                    this.error = "Falha ao conectar com o servidor. Tente novamente.";
                })
                .finally(() => {
                    this.isConnecting = false;
                    this.removeLoading(["send"]);
                });
        },
        initJsonText() {
            this.error = null;
            let value = this.jsonData;

            if (value === null || value === undefined || value === "") {
                value = this.sampleJson;
            }

            if (typeof value === "string") {
                try {
                    const parsed = JSON.parse(value);
                    this.jsonText = JSON.stringify(parsed, null, 2);
                } catch (e) {
                    this.error = "Conteúdo inicial não é um JSON válido. Edite para corrigir.";
                    this.jsonText = value;
                }
            } else {
                try {
                    this.jsonText = JSON.stringify(value, null, 2);
                } catch (e) {
                    this.error = "Erro ao formatar o JSON inicial.";
                    this.jsonText = String(value);
                }
            }
        },
        handleKeydown(event) {
            const isF = event.key === "F" || event.key === "f";

            if (isF && event.shiftKey && event.altKey) {
                event.preventDefault();
                this.formatJson();
            }
        },
        formatJson() {
            this.error = null;
            try {
                const parsed = JSON.parse(this.jsonText);
                this.jsonText = JSON.stringify(parsed, null, 2);
            } catch (e) {
                this.error = "Não foi possível formatar: JSON inválido.";
            }
        },
        downloadJson() {
            try {
                let content = this.jsonText;
                try {
                    const parsed = JSON.parse(this.jsonText);
                    content = JSON.stringify(parsed, null, 2);
                } catch (e) {
                    // Mantém o conteúdo original se não for um JSON válido
                }

                const blob = new Blob([content], {
                    type: "application/json;charset=utf-8",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = this.downloadFilename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (e) {
                this.error = "Erro ao gerar arquivo para download.";
            }
        },
    },
};
</script>

<style scoped>
.json-editor-full {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #374151;
    border-radius: 8px;
    background: #1f2125;
    padding: 14px 16px;
    color: #e5e7eb;
}

.json-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
}

.json-subtitle {
    margin: 2px 0 0 0;
    font-size: 12px;
    color: #9ca3af;
}

.json-subtitle small {
    color: #6b7280;
}

.json-buttons {
    display: flex;
    gap: 8px;
}

.btn-primary,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    border: 0;
    cursor: pointer;
    font-size: 14px;
    white-space: nowrap;
}

.btn-icon {
    font-size: 14px;
    cursor: pointer;
}

.btn-primary {
    background: #577696;
    color: #ffffff;
}

.btn-secondary {
    background: #1f2937;
    color: #e5e7eb;
    border-color: #4b5563;
}

.btn-secondary:hover {
    background: #273449;
    transform: translateY(-1px);
}

.json-status-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    font-size: 12px;
}

.status-badge {
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 500;
}

.status-ok {
    background: #14532d;
    color: #bbf7d0;
}

.status-error {
    background: #7f1d1d;
    color: #fecaca;
}

.json-meta {
    color: #6b7280;
}

.json-body {
    position: relative;
}

.json-error {
    margin-bottom: 6px;
    padding: 6px 8px;
    border-radius: 6px;
    background: #7f1d1d;
    color: #fecaca;
    font-size: 12px;
}

.json-textarea {
    width: 100%;
    min-height: 380px;
    max-height: 70vh;
    resize: vertical;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid #4b5563;
    font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
    font-size: 13px;
    line-height: 1.45;
    background: #22272E;
    color: #e5e7eb;
    box-sizing: border-box;
    white-space: pre;
    overflow: auto;
}

.connection-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.connection-modal {
    min-width: 280px;
    padding: 14px 18px;
    border-radius: 8px;
    border: 1px solid #4b5563;
    background: #1f2125;
    color: #e5e7eb;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
}

</style>
